import React from 'react';
import { Helmet } from 'react-helmet';
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE,
  COMPANY,
} from '../../config/seo';

// Renders per-page <title>/meta/canonical/Open Graph/Twitter tags plus
// JSON-LD structured data. Credits Arrayscript as the creator/publisher of
// Remembr so search engines associate the product with the company.
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  schema,
}) => {
  const url = `${SITE_URL}${path}`;
  const absoluteImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: COMPANY.name,
      url: COMPANY.url,
      sameAs: COMPANY.sameAs,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: SITE_NAME,
      url: SITE_URL,
      description: DEFAULT_DESCRIPTION,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      creator: { '@type': 'Organization', name: COMPANY.name, url: COMPANY.url },
      publisher: { '@type': 'Organization', name: COMPANY.name, url: COMPANY.url },
    },
    ...(Array.isArray(schema) ? schema : schema ? [schema] : []),
  ];

  return (
    <Helmet titleTemplate={`%s | ${SITE_NAME}`} defaultTitle={DEFAULT_TITLE}>
      {title && <title>{title}</title>}
      <meta name="description" content={description} />
      <meta name="author" content={COMPANY.name} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title || DEFAULT_TITLE} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={absoluteImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || DEFAULT_TITLE} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
};

export default SEO;
